import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["quantity", "actualQuantity", "ratio", "individualUnits", "calculatedSubtotal", "subtotalOverride", "unitPriceDisplay", "individualUnitPriceSuffix", "originalSubtotalDisplay", "originalDepositDisplay", "stateDepositDisplay"]
  static values = { ratio: Number, unitPrice: Number, individualUnitPrice: Number, deposit: Number, stateDeposit: Number }

  connect() {
    this.syncDisabledState()
    this.updateIndividualUnitsFromQuantity()
    this.updateCalculatedSubtotal()
    this.updateDepositDisplays()
  }

  ratioChanged() {
    this.syncDisabledState()
    this.updateIndividualUnitsFromQuantity()
  }

  quantityChanged() {
    this.updateIndividualUnitsFromQuantity()
    this.updateCalculatedSubtotal()
    this.updateDepositDisplays()
  }

  unitsChanged() {
    const ratio = this.currentRatio()
    const individualUnits = parseFloat(this.individualUnitsTarget.value)
    if (!ratio || isNaN(individualUnits)) return

    const preciseQuantity = individualUnits / ratio
    this.quantityTarget.value = this.round(preciseQuantity)
    this.updateCalculatedSubtotal(preciseQuantity)
    this.updateDepositDisplays(preciseQuantity)
  }

  showFullUnitPrice() {
    if (!this.hasUnitPriceDisplayTarget || !this.hasUnitPriceValue) return

    this.unitPriceDisplayTarget.value = this.unitPriceValue.toFixed(2)
    if (this.hasIndividualUnitPriceSuffixTarget) this.individualUnitPriceSuffixTarget.classList.add('d-none')
  }

  showIndividualUnitPrice() {
    if (!this.hasUnitPriceDisplayTarget || !this.hasIndividualUnitPriceValue) return

    this.unitPriceDisplayTarget.value = this.individualUnitPriceValue.toFixed(2)
    if (this.hasIndividualUnitPriceSuffixTarget) this.individualUnitPriceSuffixTarget.classList.remove('d-none')
  }

  toggleSubtotalOverride() {
    if (!this.hasSubtotalOverrideTarget || !this.hasCalculatedSubtotalTarget) return

    const overriding = this.subtotalOverrideTarget.classList.contains('d-none')
    this.subtotalOverrideTarget.classList.toggle('d-none', !overriding)
    this.calculatedSubtotalTarget.classList.toggle('d-none', overriding)

    if (overriding) {
      this.subtotalOverrideTarget.value ||= this.calculatedSubtotalTarget.value
      this.subtotalOverrideTarget.focus()
      this.updateOriginalSubtotalDisplay()
      if (this.hasOriginalSubtotalDisplayTarget) this.originalSubtotalDisplayTarget.classList.remove('d-none')
    } else {
      this.subtotalOverrideTarget.value = ''
      if (this.hasOriginalSubtotalDisplayTarget) this.originalSubtotalDisplayTarget.classList.add('d-none')
    }
  }

  updateIndividualUnitsFromQuantity() {
    if (!this.hasIndividualUnitsTarget) return

    const ratio = this.currentRatio()
    const quantity = parseFloat(this.quantityTarget.value)
    if (!ratio || isNaN(quantity)) return

    this.individualUnitsTarget.value = this.round(quantity * ratio)
  }

  updateCalculatedSubtotal(preciseQuantity) {
    if (!this.hasCalculatedSubtotalTarget || !this.hasUnitPriceValue) return

    const quantity = preciseQuantity ?? (this.hasActualQuantityTarget
      ? parseFloat(this.actualQuantityTarget.value)
      : parseFloat(this.quantityTarget.value))

    this.calculatedSubtotalTarget.value = isNaN(quantity) ? '' : (quantity * this.unitPriceValue).toFixed(2)
    this.updateOriginalSubtotalDisplay()
  }

  updateOriginalSubtotalDisplay() {
    if (!this.hasOriginalSubtotalDisplayTarget) return

    const value = this.calculatedSubtotalTarget.value
    this.originalSubtotalDisplayTarget.textContent = value ? `Original: $${ value }` : ''
  }

  updateDepositDisplays(preciseQuantity) {
    const quantity = preciseQuantity ?? parseFloat(this.quantityTarget.value)

    if (this.hasOriginalDepositDisplayTarget) {
      this.originalDepositDisplayTarget.textContent = this.hasDepositValue && !isNaN(quantity)
        ? `Original: $${ (quantity * this.depositValue).toFixed(2) }`
        : ''
    }

    if (this.hasStateDepositDisplayTarget && this.hasStateDepositValue) {
      this.stateDepositDisplayTarget.value = isNaN(quantity) ? '' : (quantity * this.stateDepositValue).toFixed(2)
    }
  }

  currentRatio() {
    if (this.hasRatioTarget) {
      const value = parseFloat(this.ratioTarget.value)
      return isNaN(value) ? null : value
    }
    return this.hasRatioValue ? this.ratioValue : null
  }

  syncDisabledState() {
    if (!this.hasIndividualUnitsTarget) return

    this.individualUnitsTarget.disabled = !this.currentRatio()
  }

  round(value) {
    return Math.round(value * 1000) / 1000
  }
}
